import React, { type CSSProperties, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

type TablePagination = {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage?: boolean;
  hasPrevPage?: boolean;
};

type TableColumn<T> = {
  key: string;
  header: ReactNode;
  render: (row: T, index: number) => ReactNode;
  tooltip?: (row: T, index: number) => string | undefined;
  width?: number | string;
  minWidth?: number | string;
  align?: 'left' | 'center' | 'right';
  headerStyle?: CSSProperties;
  cellStyle?: CSSProperties;
  clampLines?: number;
};

interface TableProps<T> {
  columns: TableColumn<T>[];
  rows: T[];
  rowKey: (row: T, index: number) => string;
  minWidth?: number | string;
  stickyHeader?: boolean;
  pagination?: TablePagination;
  onPageChange?: (page: number) => void;
  emptyState?: ReactNode;
  onRowClick?: (row: T, index: number) => void;
  rowStyle?: (row: T, index: number) => CSSProperties | undefined;
  renderExpandedRow?: (row: T, index: number, colSpan: number) => ReactNode;
}

const clampStyle = (lines: number): CSSProperties => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: lines,
  whiteSpace: 'normal',
  wordBreak: 'normal',
  overflowWrap: 'anywhere',
  lineHeight: 1.4,
  maxWidth: '100%',
  width: '100%',
});

function buildPageNumbers(totalPages: number, currentPage: number) {
  const pages: Array<number | 'ellipsis'> = [];

  if (totalPages <= 5) {
    for (let page = 1; page <= totalPages; page += 1) pages.push(page);
    return pages;
  }

  pages.push(1);

  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);

  if (start > 2) pages.push('ellipsis');
  for (let page = start; page <= end; page += 1) pages.push(page);
  if (end < totalPages - 1) pages.push('ellipsis');

  pages.push(totalPages);
  return pages;
}

export type { TableColumn, TablePagination };

function Table<T>({
  columns,
  rows,
  rowKey,
  minWidth = 900,
  stickyHeader = false,
  pagination,
  onPageChange,
  emptyState,
  onRowClick,
  rowStyle,
  renderExpandedRow,
}: TableProps<T>) {
  const canPaginate =
    Boolean(pagination) &&
    Boolean(onPageChange) &&
    (pagination?.totalPages ?? 0) >= 1;

  return (
    <div className="card" style={{ borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
      <div className="table-responsive">
        <table
          style={{
            minWidth,
            borderCollapse: 'collapse',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          <thead>
            <tr style={{ background: 'var(--color-primary)', color: 'white' }}>
              {columns.map((column) => (
                <th
                  key={column.key}
                  style={{
                    padding: '12px 16px',
                    textAlign: column.align ?? 'left',
                    fontWeight: 600,
                    fontSize: 'var(--font-size-sm)',
                    letterSpacing: '0.02em',
                    whiteSpace: 'nowrap',
                    borderBottom: '2px solid var(--color-primary-dark)',
                    width: column.width,
                    minWidth: column.minWidth,
                    position: stickyHeader ? 'sticky' : undefined,
                    top: stickyHeader ? 0 : undefined,
                    zIndex: stickyHeader ? 2 : undefined,
                    ...column.headerStyle,
                  }}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} style={{ padding: 0 }}>
                  {emptyState}
                </td>
              </tr>
            ) : (
              rows.map((row, rowIndex) => {
                const baseRowColor =
                  rowIndex % 2 === 0 ? 'white' : 'var(--color-gray-50)';
                const customRowStyle = rowStyle?.(row, rowIndex);
                const expandedRow = renderExpandedRow?.(row, rowIndex, columns.length);

                return (
                  <React.Fragment key={rowKey(row, rowIndex)}>
                    <tr
                      style={{
                        background: baseRowColor,
                        borderBottom: '1px solid var(--color-gray-100)',
                        transition: 'background 0.15s',
                        cursor: onRowClick ? 'pointer' : customRowStyle?.cursor,
                        ...customRowStyle,
                      }}
                      onClick={onRowClick ? () => onRowClick(row, rowIndex) : undefined}
                      onMouseEnter={(event) => {
                        event.currentTarget.style.background = 'var(--color-primary-50)';
                      }}
                      onMouseLeave={(event) => {
                        event.currentTarget.style.background =
                          String(customRowStyle?.background ?? baseRowColor);
                      }}
                    >
                      {columns.map((column) => {
                        const tooltip = column.tooltip?.(row, rowIndex);

                        return (
                          <td
                            key={column.key}
                            title={tooltip}
                            style={{
                              padding: '10px 16px',
                              color: 'var(--color-gray-700)',
                              textAlign: column.align ?? 'left',
                              verticalAlign: 'top',
                              width: column.width,
                              minWidth: column.minWidth,
                              ...column.cellStyle,
                            }}
                          >
                            <div style={column.clampLines ? clampStyle(column.clampLines) : undefined}>
                              {column.render(row, rowIndex)}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                    {expandedRow}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {canPaginate && pagination ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--spacing-3)',
            padding: 'var(--spacing-3) var(--spacing-4)',
            borderTop: '1px solid var(--color-gray-200)',
            flexWrap: 'wrap',
          }}
        >
          <p
            style={{
              color: 'var(--color-gray-500)',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            Showing {Math.min((pagination.page - 1) * pagination.limit + 1, pagination.total)}-
            {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
          </p>

          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
            <button
              type="button"
              onClick={() => onPageChange?.(Math.max(1, pagination.page - 1))}
              disabled={pagination.hasPrevPage === false || pagination.page <= 1}
              className="px-2 py-1.5 rounded border border-gray-200 text-md font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 flex items-center gap-1"
            >
              <ChevronLeft size={16} />
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {buildPageNumbers(pagination.totalPages, pagination.page).map((page, index) =>
                page === 'ellipsis' ? (
                  <span
                    key={`ellipsis-${index}`}
                    style={{
                      padding: '4px 6px',
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--color-gray-500)',
                    }}
                  >
                    ...
                  </span>
                ) : (
                  <button
                    key={page}
                    type="button"
                    onClick={() => onPageChange?.(page)}
                    disabled={page === pagination.page}
                    style={{
                      minWidth: 32,
                      padding: '4px 8px',
                      borderRadius: 'var(--radius-md)',
                      border:
                        page === pagination.page
                          ? '1px solid var(--color-primary)'
                          : '1px solid var(--color-gray-200)',
                      background:
                        page === pagination.page ? 'var(--color-primary)' : 'white',
                      color: page === pagination.page ? 'white' : 'var(--color-gray-700)',
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 500,
                      cursor: page === pagination.page ? 'default' : 'pointer',
                    }}
                  >
                    {page}
                  </button>
                )
              )}
            </div>

            <button
              type="button"
              onClick={() =>
                onPageChange?.(Math.min(pagination.totalPages, pagination.page + 1))
              }
              disabled={
                pagination.hasNextPage === false || pagination.page >= pagination.totalPages
              }
              className="px-2 py-1.5 rounded border border-gray-200 text-md font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 flex items-center gap-1"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default Table;
